// frontend/src/App.js
import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import { SnackbarProvider } from 'notistack';
import { QueryClient, QueryClientProvider } from 'react-query';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

// Import animations
import './styles/animations.css';
import './styles/print.css';

// Context Providers
import { AuthProvider } from './contexts/AuthContext';
import { PermissionProvider } from './contexts/PermissionContext';
import { FileProvider } from './contexts/FileContext';
import { CombinedThemeProvider } from './contexts/ThemeContext';
import { DeviceProvider } from './contexts/DeviceContext';
import { NavigationProvider } from './contexts/NavigationContext';
import { SiteFilterProvider } from './contexts/SiteFilterContext';
import { CommandRegistryProvider } from './contexts/CommandRegistry';

// Notification components
import NotificationPrompt from './components/NotificationPrompt';
import DesktopNotificationOverlay from './components/DesktopNotificationOverlay';
import SwUpdatePrompt from './components/SwUpdatePrompt';
import OfflineBanner from './components/OfflineBanner';
import SnackbarCloseButton from './components/SnackbarCloseButton';
import notificationService from './services/notificationService';

// Layout Components (always needed)
import Layout from './components/Layout';
import PrivateRoute from './components/PrivateRoute';
import PermissionRoute from './components/PermissionRoute';
import CompanyPermissionRoute from './components/CompanyPermissionRoute';
import InstallPWA from './components/InstallPWA';
import ErrorBoundary from './components/ErrorBoundary';
import RouteErrorBoundary from './components/RouteErrorBoundary';
import SkipLink from './components/SkipLink';
import PageProgressBar from './components/PageProgressBar';
import ScrollRestoration from './components/ScrollRestoration';
import ImpersonationBannerWrapper from './components/ImpersonationBannerWrapper';
import BrandedLoader from './components/BrandedLoader';

// Eagerly-loaded critical pages — first paint + auth flow
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import AuthError from './pages/AuthError';
import PermissionDenied from './pages/PermissionDenied';
import Dashboard from './pages/Dashboard';
import NotFound from './pages/NotFound';
import Health from './pages/Health';

// Lazy-loaded pages. Splitting these out of the initial bundle keeps
// the first paint under 1MB; heavy deps (leaflet, reactflow, xlsx) only
// download when the route is visited. `InterventionDetailResponsive`
// exports default — match that name through `.then(m => ...)` if the
// export shape ever changes.
const Settings = lazy(() => import('./pages/Settings'));
const TicketManagementSystem = lazy(() => import('./pages/Tickets'));
const TicketsManagement = lazy(() => import('./pages/TicketsManagement'));
const LocationManagement = lazy(() => import('./pages/LocationManagement/index'));
const DeviceManagement = lazy(() => import('./pages/DeviceManagement/indexOptimized'));
const UserManagement = lazy(() => import('./pages/UserManagement/index'));
const Help = lazy(() => import('./pages/Help'));
const ReleaseNotesViewer = lazy(() => import('./pages/ReleaseNotesViewer'));
const DeveloperInfo = lazy(() => import('./pages/DeveloperInfo'));
const CameraRequests = lazy(() => import('./pages/CameraRequests/index'));
const RequestDetail = lazy(() => import('./pages/CameraRequests/RequestDetail'));
const CompanyManagement = lazy(() => import('./pages/CompanyManagement/index'));
const FieldInterventions = lazy(() => import('./pages/FieldInterventions/index'));
const InterventionDetail = lazy(() => import('./pages/FieldInterventions/InterventionDetailResponsive'));
const CreateIntervention = lazy(() => import('./pages/FieldInterventions/CreateIntervention'));
const Notifications = lazy(() => import('./pages/Notifications'));
const NotificationManagement = lazy(() => import('./pages/NotificationManagement'));
const IntegrationManagement = lazy(() => import('./pages/IntegrationManagement'));
const EmergencyServicesManagement = lazy(() => import('./pages/EmergencyServicesManagement'));
const CommanderList = lazy(() => import('./components/Integrations/TwoN/CommanderList'));
const FirmwareManagement = lazy(() => import('./pages/FirmwareManagement'));
const AlarmsDashboard = lazy(() => import('./pages/AlarmsDashboard'));
const AlarmDetails = lazy(() => import('./pages/AlarmDetails'));
// T036/T037 — friendly pages for the public bearer-token error states.
// Spec: specs/20260428-secure-public-alarm-access/plan.md §5.2.
const AlarmLinkExpired = lazy(() => import('./pages/AlarmDetails/AlarmLinkExpired'));
const AlarmFinished = lazy(() => import('./pages/AlarmDetails/AlarmFinished'));
const AlarmNotFound = lazy(() => import('./pages/AlarmDetails/AlarmNotFound'));
const AlarmError = lazy(() => import('./pages/AlarmDetails/AlarmError'));
const AlarmFlows = lazy(() => import('./pages/AlarmFlows'));
const AlarmFlowEditor = lazy(() => import('./pages/AlarmFlows/AlarmFlowEditor'));
const AlarmFlowBindings = lazy(() => import('./pages/AlarmFlows/AlarmFlowBindings'));
// IndoorMapping pulls in the full Leaflet bundle via `leaflet/dist/leaflet.css`,
// so we load the CSS lazily inside the page module instead of at app boot.
const IndoorMapping = lazy(() => import('./pages/IndoorMapping'));
const IndoorNavigationPreview = lazy(() => import('./pages/IndoorNavigationPreview'));
const ServiceManagement = lazy(() => import('./pages/ServiceManagement'));
const DeviceTypeManagement = lazy(() => import('./pages/DeviceTypeManagement'));
const DeviceLinking = lazy(() => import('./pages/DeviceLinking'));
const BaseIPManagement = lazy(() => import('./pages/BaseIPManagement'));
const BusinessUpdate = lazy(() => import('./pages/BusinessUpdate'));
const SalesPresentation = lazy(() => import('./pages/SalesPresentation'));
const MonitoringDashboard = lazy(() => import('./pages/MonitoringDashboard'));
const PersonsManagement = lazy(() => import('./pages/PersonsManagement'));
const PersonTypeManagement = lazy(() => import('./pages/PersonTypeManagement'));
const EnOceanButtonManagement = lazy(() => import('./pages/EnOceanButtonManagement'));
const EnOceanButtonImport = lazy(() => import('./pages/EnOceanButtonImport'));
const LocationDataImport = lazy(() => import('./pages/LocationDataImport'));
const UnitAudit = lazy(() => import('./pages/UnitAudit'));
const QbusOutputTypeManagement = lazy(() => import('./pages/QbusOutputTypeManagement'));
const Automations = lazy(() => import('./pages/Automations'));
const QbusFriendlyNames = lazy(() => import('./pages/QbusFriendlyNames'));
const QbusMonitor = lazy(() => import('./pages/QbusMonitor'));
const TelephonyManagement = lazy(() => import('./pages/TelephonyManagement'));
const EinkDesigner = lazy(() => import('./pages/EinkDesigner'));

// Create query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function App() {
  useEffect(() => {
    // Initialize notification service
    notificationService.initialize();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <CombinedThemeProvider>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <CssBaseline />
          <SnackbarProvider
            // Raised from 3 → 5 so a burst of alarm-related toasts
            // doesn't silently drop earlier messages. Still low enough
            // that the stack doesn't dominate the screen.
            maxSnack={5}
            // Collapse repeat messages (e.g. "Toggled" × 3) into one.
            preventDuplicate
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            // Lift snackbars above the fixed mobile bottom nav + iOS
            // home-indicator so users can still read & dismiss them.
            // On md+ the regular bottom-right position is unchanged.
            classes={{ containerRoot: 'caro-snackbar-root' }}
            // Default dismiss button on every snackbar. Snackbars that
            // supply their own `action` override this (notistack
            // precedence), so Undo / Reload-app toasts keep their own UX.
            action={(key) => <SnackbarCloseButton snackbarKey={key} />}
          >
            <AuthProvider>
              <PermissionProvider>
                <FileProvider>
                  <DeviceProvider>
                    <NavigationProvider>
                      <SiteFilterProvider>
                      <CommandRegistryProvider>
                      <ErrorBoundary>
                      <Router>
                        <SkipLink />
                        <PageProgressBar />
                        <ScrollRestoration />
                        {/* Impersonation Banner (shows when admin is viewing as another user) */}
                        <ImpersonationBannerWrapper />

                        {/* PWA Install Prompt */}
                        <InstallPWA />

                        {/* Service-worker update toast */}
                        <SwUpdatePrompt />

                        {/* Offline banner */}
                        <OfflineBanner />

                        {/* Notification Permission Prompt */}
                        <NotificationPrompt />

                        {/* Desktop Notification Overlay (for PWA) */}
                        <DesktopNotificationOverlay />

                        <Suspense fallback={
                          <BrandedLoader label="Loading…" sx={{ minHeight: '60vh' }} />
                        }>
                        <RouteErrorBoundary>
                        <Routes>
                          {/* Public routes */}
                          <Route path="/login" element={<Login />} />
                          <Route path="/health" element={<Health />} />

                          {/* Public alarm access via UUID — bearer-token guarded.
                              The token rides in the URL fragment (`#t=<hex>`) and
                              is read by `usePublicAlarmToken` inside <AlarmDetails>.
                              Spec: specs/20260428-secure-public-alarm-access/plan.md §5.2.
                              Friendly error routes — registered BEFORE `:uuid` so the
                              literal paths match first. T033/T036/T037. */}
                          <Route path="/alarm/expired" element={<AlarmLinkExpired />} />
                          <Route path="/alarm/finished" element={<AlarmFinished />} />
                          <Route path="/alarm/not-found" element={<AlarmNotFound />} />
                          <Route path="/alarm/error" element={<AlarmError />} />
                          <Route path="/alarm/:uuid" element={<AlarmDetails />} />
                          {/* T033 — legacy `/alarms/uuid/:uuid` alias deleted in
                              the hard-cutover deploy (plan §9.3, Clarification #10). */}

                          {/* Auth Callback Routes */}
                          <Route path="/auth-callback" element={<AuthCallback />} />
                          <Route path="/auth-error" element={<AuthError />} />
                          <Route path="/permission-denied" element={<PermissionDenied />} />

                          {/* Private routes */}
                          <Route element={<PrivateRoute />}>
                            <Route element={<Layout />}>
                              <Route path="/" element={<Navigate to="/dashboard" replace />} />
                              <Route path="/dashboard" element={<Dashboard />} />


                            <Route element={<PermissionRoute
                              permission="locations.view"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/locations" element={<LocationManagement />} />
                            </Route>

                            <Route element={<CompanyPermissionRoute
                              permission="devices.view"
                              allowedCompanyIds={1}
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/devices" element={<DeviceManagement />} />
                            </Route>

                            <Route element={<PermissionRoute
                              permission="tickets.view"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/tickets" element={<TicketsManagement />} />
                              <Route path="/tickets-new" element={<TicketsManagement />} />
                              <Route path="/tickets-old" element={<TicketManagementSystem />} />
                            </Route>

                            {/* Alarms Dashboard - Admin only */}
                            <Route path="/alarms" element={<AlarmsDashboard />} />
                            <Route path="/alarms/:id" element={<AlarmDetails />} />

                            {/* Alarm Flow Configuration */}
                            <Route path="/alarm-flows" element={<AlarmFlows />} />
                            <Route path="/alarm-flows/new" element={<AlarmFlowEditor />} />
                            <Route path="/alarm-flows/bindings" element={<AlarmFlowBindings />} />
                            <Route path="/alarm-flows/:id" element={<AlarmFlowEditor />} />

                            <Route element={<PermissionRoute
                              permission="camera_requests.view"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/camera-requests" element={<CameraRequests />} />
                              <Route path="/camera-requests/:id" element={<RequestDetail />} />
                            </Route>

                            <Route element={<PermissionRoute
                              permission="field_interventions.view"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/field-interventions" element={<FieldInterventions />} />
                              <Route path="/field-interventions/new" element={<CreateIntervention />} />
                              <Route path="/field-interventions/:id" element={<InterventionDetail />} />
                            </Route>

                            <Route element={<PermissionRoute
                              permission="users.permissions.manage"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/users" element={<UserManagement />} />
                            </Route>

                            {/* Persons Management */}
                            <Route element={<PermissionRoute
                              permission="persons.view"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/persons" element={<PersonsManagement />} />
                            </Route>

                            {/* Person Type Management (admin) */}
                            <Route element={<PermissionRoute
                              permission="persontypes.manage"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/person-type-management" element={<PersonTypeManagement />} />
                            </Route>

                            {/* Admin-only routes - admin check is handled within the components */}
                            <Route path="/company-management" element={<CompanyManagement />} />
                            <Route path="/service-management" element={<ServiceManagement />} />
                            <Route path="/device-type-management" element={<DeviceTypeManagement />} />
                            <Route path="/device-linking" element={<DeviceLinking />} />
                            <Route path="/baseip-management" element={<BaseIPManagement />} />
                            <Route path="/enocean-buttons" element={<EnOceanButtonManagement />} />
                            <Route path="/enocean-import" element={<EnOceanButtonImport />} />
                            <Route path="/location-data-import" element={<LocationDataImport />} />
                            <Route path="/unit-audit" element={<UnitAudit />} />
                            <Route path="/qbus-output-types" element={<QbusOutputTypeManagement />} />
                            <Route path="/automations" element={<Automations />} />
                            <Route path="/qbus-friendly-names" element={<QbusFriendlyNames />} />
                            <Route path="/qbus-monitor" element={<QbusMonitor />} />
                            <Route path="/telephony" element={<TelephonyManagement />} />
                            <Route path="/eink-designer" element={<EinkDesigner />} />

                            <Route element={<PermissionRoute
                              permission="users.permissions.manage"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/integration-management" element={<IntegrationManagement />} />
                              <Route path="/admin/integrations/emergency-services" element={<EmergencyServicesManagement />} />
                              <Route path="/admin/integrations/twon/commanders" element={<CommanderList />} />
                            </Route>

                            <Route element={<PermissionRoute
                              permission={["devices.edit", "devices.create"]}
                              requireAll={false}
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/firmware-management" element={<FirmwareManagement />} />
                            </Route>

                            <Route element={<PermissionRoute
                              permission="users.permissions.manage"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/notification-management" element={<NotificationManagement />} />
                            </Route>

                            {/* <Route element={<PermissionRoute
                              permission="users.permissions.manage"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/translations" element={<TranslationManagement />} />
                            </Route> */}

                            <Route path="/settings" element={<Settings />} />
                            <Route path="/notifications" element={<Notifications />} />
                            <Route path="/monitoring" element={<MonitoringDashboard />} />
                            <Route path="/help" element={<Help />} />
                            <Route path="/release-notes/:version" element={<ReleaseNotesViewer />} />

                            <Route element={<PermissionRoute
                              permission="users.permissions.manage"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/developer-info" element={<DeveloperInfo />} />
                            </Route>

                            {/* Indoor Mapping Admin - Admin only */}
                            <Route element={<PermissionRoute
                              permission="users.permissions.manage"
                              fallbackPath="/permission-denied"
                            />}>
                              <Route path="/indoor-mapping" element={<IndoorMapping />} />
                              <Route path="/indoor-navigation-preview" element={<IndoorNavigationPreview />} />
                            </Route>

                            </Route>

                          {/* Fullscreen presentations - outside Layout for no navbar/header/footer */}
                          <Route element={<PermissionRoute
                            permission="system.admin"
                            fallbackPath="/permission-denied"
                          />}>
                            <Route path="/business-update" element={<BusinessUpdate />} />
                            <Route path="/sales-presentation" element={<SalesPresentation />} />
                          </Route>
                          </Route>

                          {/* Catch-all - redirect to login */}
                          <Route path="*" element={<NotFound />} />
                        </Routes>
                        </RouteErrorBoundary>
                        </Suspense>
                      </Router>
                      </ErrorBoundary>
                      </CommandRegistryProvider>
                      </SiteFilterProvider>
                    </NavigationProvider>
                  </DeviceProvider>
                </FileProvider>
              </PermissionProvider>
            </AuthProvider>
          </SnackbarProvider>
        </LocalizationProvider>
      </CombinedThemeProvider>
    </QueryClientProvider>
  );
}

export default App;